import {
  useCallback,
  useContext,
  useRef,
  useState,
  useEffect,
} from 'react';
import moment from 'moment';
import t from 'react-translate';
import { pick } from 'lodash';
import { css } from '@emotion/react';
import * as yup from 'yup';

// redux
import { ComponentType, LectureComponent } from 'redux/schemas/models/lecture-component';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { getCurrentCourse } from 'redux/selectors/course';
import { RootState } from 'redux/schemas';
import { updateLectureComponent } from 'redux/actions/lecture-pages';
import { getAiRolePlay } from 'redux/selectors/timeline';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';

import BaseLectureComponentContext from 'lecture_pages/directives/components/base-lecture-component/context';
import { DeepPartial } from 'utility-types';
import { NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import ActivityTitle from 'shared/components/activity/activity-title';
import ActivityStatus from 'shared/components/activity/activity-status';
import { LecturePageMode, LectureComponentProps } from 'lecture_pages/components';
import LectureComponentHeaderInput from 'shared/components/lecture-component-header-input';
import { Button } from 'react-bootstrap';
import LectureComponentDeadline from 'components/lecture-component-deadline';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { black, gray7, hexToRgbaString } from 'styles/global_defaults/colors';
import ViewValidationError from 'quizzes/components/view-validation-error';
import { quarterSpacing } from 'styles/global_defaults/scaffolding';
import { headerLineHeight } from 'styles/global_defaults/fonts';
import ActivityPoints from 'shared/components/activity/activity-points';
import EditInstructionFormModal from './ai-config-modal/set-up-instructions/edit-instruction-form-modal';
import EditAiInstructionContext from './ai-config-modal/set-up-instructions/context';
import modalStyles from './ai-config-modal/styles';
import ConversationModalContent from './ai-conversation-modal';
import AiConversationContext from './ai-conversation-modal/context';

type AiRolePlayLectureComponentType = LectureComponent<ComponentType.AI_ROLE_PLAY>;
const MODAL_WIDTH = 1080;

const AiRolePlayLectureComponent = (props: LectureComponentProps<ComponentType.AI_ROLE_PLAY>) => {
  const {
    mode,
    currentLecture,
    lectureComponent,
  } = props;

  const styles = css`
    .role-play-start-button {
      margin-top: 80px;
    }
    .description-container {
      width: 100%;
      text-align: left;
      .text-container {
        span {
          vertical-align: top !important;
        }
      }
      .min-h-0 {
        min-height: 0px !important;
      }
      .bs4-input-group-append {
        display: none;
      }
      textarea {
        padding: ${quarterSpacing}px !important;
        background-color: transparent !important;
        line-height: ${headerLineHeight}px;
      }
      textarea:focus, textarea:hover {
        background-color: ${mode === LecturePageMode.EDIT ? gray7 : 'transparent'} !important;
      }
      .bs4-form-control {
        border: none;
        text-align: left;
        box-sizing: border-box;
        padding: 0;
        overflow: hidden;
      }
    }
  `;

  const conversationModalStyles = css`
    .close {
      top: 0;
      right: 0;
    }
    .bs4-modal-content {
      background-color: ${hexToRgbaString(black, 0.8)} !important;
    }
  `;

  const dispatch = useAppDispatch();
  const currentLectureId = currentLecture.id;

  const aiRolePlay = useSelector((state) => getAiRolePlay(state, lectureComponent.aiRolePlay));
  const { pointsConfiguration } = aiRolePlay || {};
  const currentCourse = useSelector(getCurrentCourse);
  const currentCatalogId = useSelector((state) => state.app.currentCatalogId);
  const [showEditAiInstruction, setShowEditAiInstruction] = useState(false);
  const [showConversationModal, setShowConversationModal] = useState(false);
  const [isConversationStarted, setIsConversationStarted] = useState(false);
  const [showScenarioDetails, setShowScenarioDetails] = useState(true);

  const forwardOnModalCloseRef = useRef(null);

  const isCurrentCourseSelfPaced = currentCourse.isSelfPaced;
  const lectureComponentId = lectureComponent.id;
  const {
    expirationDate,
  } = aiRolePlay || {};

  const [title, setTitle] = useState<string>(aiRolePlay?.title);

  const descriptionValidationSchema = yup.object().shape({
    description: yup.string().required(t.VALIDATION.REQUIRED())
      .max(10000, t.VALIDATION.MAX_LENGTH('10000')),
  });

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      description: aiRolePlay?.description || '',
    },
    resolver: yupResolver(descriptionValidationSchema),
  });
  const { formState: { errors } } = methods;

  const { sharedProps, setSharedProps } = useContext(BaseLectureComponentContext);
  const initialSharedProps = useRef(sharedProps).current;

  const lecturePageId = useSelector((state: RootState) => state.app.lecturePage.currentLectureId);
  const lecturePage = useSelector(state => state.models.lecturePages[lecturePageId]);

  const openEditAiInstruction = useCallback(() => {
    setShowEditAiInstruction(true);
  }, []);

  const saveComponent = useCallback((componentDataPatch: DeepPartial<AiRolePlayLectureComponentType['aiRolePlay']>) => {
    const componentDataUpdate = {
      catalogId: currentCatalogId,
      lecturePageId: currentLectureId,
      componentData: {
        id: lectureComponentId,
        type: lectureComponent.type,
        aiRolePlay: {
          ...pick(aiRolePlay, [
            'isTodo',
            'hardDeadline',
            'expirationDate',
          ]),
          ...componentDataPatch,
        },
      },
    };

    return dispatch(updateLectureComponent(componentDataUpdate));
  }, [
    dispatch,
    aiRolePlay,
    currentLectureId,
    currentCatalogId,
    lectureComponentId,
    lectureComponent.type,
  ]);

  useEffect(() => {
    const options: NvDropdownOption[] = [{
      type: 'text',
      callback: openEditAiInstruction,
      text: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.EDIT_DROPDOWN.EDIT_INSTRUCTION(),
      tooltip: {
        enabled: !!lecturePage.isLinked,
        text: t.LECTURE_PAGES.COMPONENTS.SET_UP_FROM_COLLECTION_TOOLTIP(),
        placement: 'left',
        offset: 20,
      },
    }];

    if (!isCurrentCourseSelfPaced) {
      options.unshift(
        { type: 'text',
          text: expirationDate ? t.LECTURE_PAGES.COMPONENTS.DROPDOWN.REMOVE_DEADLINE() : t.LECTURE_PAGES.COMPONENTS.DROPDOWN.ADD_DEADLINE(),
          callback: () => {
            saveComponent({
              expirationDate: expirationDate
                ? null
                : moment(currentLecture.releaseDate).add(1, 'weeks').endOf('day').toISOString(),
            });
          },
        },
        {
          type: 'divider',
        },
      );
    }

    setSharedProps({
      ...initialSharedProps,
      extraOptions: {
        mode: 'prepend',
        renderOnMount: true,
        options,
      },
    });
  }, [
    saveComponent,
    setSharedProps,
    expirationDate,
    openEditAiInstruction,
    initialSharedProps,
    isCurrentCourseSelfPaced,
    currentLecture.releaseDate,
  ]);

  const handleTitleBlur = () => saveComponent({
    title,
  });
  const handleTitleInputChange = (newTitle: string) => setTitle(newTitle);

  const handleCloseModal = () => {
    setShowEditAiInstruction(false);
  };

  const handleDescriptionBlur = (newDescription: string) => {
    if (errors.description) {
      return;
    }
    saveComponent({
      description: newDescription,
    });
  };

  const showDescriptionMaxCharLimitError = () => errors.description?.type === 'max';

  const handleOpenRolePlay = () => {
    setShowConversationModal(true);
  };

  const handleExitChat = useCallback(() => {
    dispatch(openConfirmationDialog({
      title: t.FORM.ARE_YOU_SURE(),
      bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.MESSAGE(),
      confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.CONFIRM(),
      cancelText: t.FORM.CANCEL(),
      onConfirm: () => {
        setShowConversationModal(false);
        setIsConversationStarted(false);
        setShowScenarioDetails(true);
      },
    }));
  }, [dispatch]);

  return (
    <div
      css={styles}
      className='d-flex flex-column align-items-center pb-6'
    >
      <div className='mb-2'>
        <ActivityStatus icon='role-play' status={aiRolePlay.progress} />
      </div>
      <ActivityTitle
        status={aiRolePlay.progress}
        statusesTexts={{
          not_started: 'AI Role Play',
          completed: t.FORM.DONE(),
        }}
      />
      <LectureComponentHeaderInput
        required
        value={title}
        editable={mode === LecturePageMode.EDIT}
        onBlur={handleTitleBlur}
        onChange={handleTitleInputChange}
      />
      {
        pointsConfiguration
          && !!pointsConfiguration.points
          && (
            <div
              className='activity-points mb-2'
            >
              <ActivityPoints
                pointsReceived={pointsConfiguration.pointsReceived}
                totalPoints={pointsConfiguration.points}
              />
            </div>
          )
      }
      <div className={expirationDate ? 'mb-2' : ''}>
        <LectureComponentDeadline
          isEdit={mode === LecturePageMode.EDIT}
          catalogId={currentCatalogId}
          componentName='ai-role-play'
          lecturePageId={currentLecture.id}
          expirationDate={expirationDate}
          hasHardDeadlines={aiRolePlay?.hardDeadline}
          onHardDeadlineChange={(hardDeadline) => {
            saveComponent({ hardDeadline });
          }}
          onDeadlineChange={(date) => {
            saveComponent({ expirationDate: date?.toISOString() });
          }}
          isDeadlineEditable={mode === LecturePageMode.EDIT}
        />
      </div>
      <FormProvider {...methods}>
        <NvTextArea
          readOnly={mode !== LecturePageMode.EDIT}
          name='description'
          className='description-container mt-4'
          withForm
          onBlur={(e) => handleDescriptionBlur(e.target.value)}
          required
        />
        {showDescriptionMaxCharLimitError() && (
          <div className='d-flex justify-content-end w-100'>
            <ViewValidationError characterLimit='10,000' />
          </div>
        )}
      </FormProvider>
      <Button
        variant='primary'
        className='role-play-start-button'
        disabled={mode !== LecturePageMode.VIEW}
        onClick={handleOpenRolePlay}
      >
        {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.START_AI_ROLE_PLAY()}
      </Button>
      <NvModal
        show={showEditAiInstruction}
        width={MODAL_WIDTH}
        type={ModalType.DYNAMIC}
        onClose={() => {
          if (forwardOnModalCloseRef.current) {
            forwardOnModalCloseRef.current();
          }
        }}
        header={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.EDIT_DROPDOWN.EDIT_INSTRUCTION()}
        body={(
          <div css={modalStyles}>
            <EditAiInstructionContext.Provider value={{
              showEditAiInstruction,
              setShowEditAiInstruction,
              forwardOnModalCloseRef,
              aiRolePlayId: aiRolePlay.id,
            }}
            >
              <EditInstructionFormModal
                saveComponent={saveComponent}
                lectureComponent={lectureComponent}
              />
            </EditAiInstructionContext.Provider>
          </div>

        )}
      />
      <NvModal
        show={showConversationModal}
        modalStyles={conversationModalStyles}
        width={MODAL_WIDTH}
        type={ModalType.NO_DIALOG}
        onClose={handleExitChat}
        body={(
          <AiConversationContext.Provider
            value={{
              handleExitChat,
              isConversationStarted,
              setIsConversationStarted,
              showScenarioDetails,
              setShowScenarioDetails,
              aiRolePlayId: aiRolePlay.id,
            }}
          >
            <ConversationModalContent />
          </AiConversationContext.Provider>
        )}
      />
    </div>
  );
};

export default AiRolePlayLectureComponent;
